import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { LOCATIONS, HERO_BG } from '../data/mockData';
import { getRoutes } from '../api';
import { Calendar, Users, MapPin, ArrowRight, Check } from 'lucide-react';
import ImageMarquee from './ImageMarquee';

const Hero = () => {
  const [fromLocation, setFromLocation] = useState('');
  const [toLocation, setToLocation] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [pax, setPax] = useState('2');
  const [price, setPrice] = useState(null);
  const [allRoutes, setAllRoutes] = useState([]);

  useEffect(() => {
    // Fetch routes from backend on mount
    const fetchRoutes = async () => {
        const routes = await getRoutes();
        setAllRoutes(routes);
    };
    fetchRoutes();
  }, []);

  // Auto-calculate price when locations change
  useEffect(() => {
    if (fromLocation && toLocation) {
      // Find direct match in backend data
      let route = allRoutes.find(r => 
        (r.from === fromLocation && r.to === toLocation) || 
        (r.from === toLocation && r.to === fromLocation)
      );
      
      if (route) {
        setPrice(route.price);
      } else {
        setPrice(null);
      }
    }
  }, [fromLocation, toLocation, allRoutes]);

  const handleWhatsAppClick = () => {
    const phoneNumber = "905468953037";
    let message = `Merhaba Şahin VIP Transfer, rezervasyon yapmak istiyorum. %0A%0A`;
    message += `📍 Nereden: ${fromLocation} %0A`;
    message += `📍 Nereye: ${toLocation} %0A`;
    message += `📅 Tarih: ${date} %0A`;
    message += `⏰ Saat: ${time} %0A`;
    message += `👥 Kişi Sayısı: ${pax} %0A`;
    
    if (price) {
        message += `💰 Tahmini Fiyat: ${price} TL %0A`;
    } else {
        message += `💰 Fiyat Bilgisi Almak İstiyorum. %0A`;
    }

    window.open(`https://wa.me/${phoneNumber}?text=${message}`, '_blank');
  };

  return (
    <div id="hero" className="relative min-h-screen flex flex-col pt-20 overflow-hidden bg-black">
      {/* Background Image with "Alla Pulla" Effects */}
      <div className="absolute inset-0 z-0 overflow-hidden h-full">
        <img 
          src={HERO_BG}
          alt="Antalya VIP Transfer" 
          className="w-full h-full object-cover animate-scale-slow filter brightness-[0.5] contrast-[1.2] saturate-[1.3]" 
        />
        {/* Luxury Gold/Black Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent opacity-90"></div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-yellow-900/10 via-black/40 to-black opacity-60"></div>
      </div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center flex-grow py-10">
        
        {/* Reservation Form */}
        <div className="w-full max-w-md mx-auto order-1 lg:order-2 lg:ml-auto">
          <Card className="bg-black/60 backdrop-blur-xl border-yellow-500/20 shadow-2xl shadow-yellow-900/20">
            <CardHeader className="space-y-1 pb-4 border-b border-white/5">
              <CardTitle className="text-2xl text-white text-center flex items-center justify-center gap-2">
                 <span className="bg-yellow-500 w-2 h-2 rounded-full animate-ping"></span>
                 Hızlı Rezervasyon
              </CardTitle>
              <p className="text-center text-gray-400 text-sm">Transferinizi hemen planlayın</p>
            </CardHeader>
            <CardContent className="space-y-4 pt-4">
              
              <div className="grid gap-2">
                <label className="text-xs font-bold uppercase tracking-wider text-gray-400 flex items-center gap-2">
                    <MapPin className="h-3 w-3 text-yellow-500" /> Nereden
                </label>
                <Select value={fromLocation} onValueChange={setFromLocation}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white focus:ring-yellow-500/50 h-11">
                    <SelectValue placeholder="Seçiniz..." />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {LOCATIONS.map(loc => (
                        <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-2">
                <label className="text-xs font-bold uppercase tracking-wider text-gray-400 flex items-center gap-2">
                    <MapPin className="h-3 w-3 text-red-500" /> Nereye
                </label>
                <Select value={toLocation} onValueChange={setToLocation}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white focus:ring-yellow-500/50 h-11">
                    <SelectValue placeholder="Seçiniz..." />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {LOCATIONS.map(loc => (
                        <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                 <div className="grid gap-2">
                    <label className="text-xs font-bold uppercase tracking-wider text-gray-400 flex items-center gap-2">
                        <Calendar className="h-3 w-3 text-yellow-500" /> Tarih
                    </label>
                    <Input 
                        type="date" 
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                        className="bg-white/5 border-white/10 text-white block w-full h-11" 
                    />
                 </div>
                 <div className="grid gap-2">
                    <label className="text-xs font-bold uppercase tracking-wider text-gray-400 flex items-center gap-2">
                        <ArrowRight className="h-3 w-3 text-yellow-500" /> Saat
                    </label>
                    <Input 
                        type="time" 
                        value={time}
                        onChange={(e) => setTime(e.target.value)}
                        className="bg-white/5 border-white/10 text-white block w-full h-11" 
                    />
                 </div>
              </div>

              <div className="grid gap-2">
                <label className="text-xs font-bold uppercase tracking-wider text-gray-400 flex items-center gap-2">
                    <Users className="h-3 w-3 text-yellow-500" /> Kişi Sayısı
                </label>
                <Select value={pax} onValueChange={setPax}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white focus:ring-yellow-500/50 h-11">
                    <SelectValue placeholder="Seçiniz..." />
                  </SelectTrigger>
                  <SelectContent>
                    {[1,2,3,4,5,6,7,8,9,10,11,12,13].map(num => (
                        <SelectItem key={num} value={num.toString()}>{num} Kişi</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {price && (
                <div className="bg-gradient-to-r from-yellow-500/10 to-yellow-600/10 border border-yellow-500/30 rounded-lg p-3 text-center animate-in fade-in zoom-in duration-300">
                    <span className="text-gray-300 text-xs block uppercase tracking-widest">Tahmini Tutar</span>
                    <span className="text-3xl font-black text-yellow-400 drop-shadow-sm">{price} TL</span>
                    <span className="text-[10px] text-gray-400 block mt-1 uppercase">*Araç başı özel fiyat</span>
                </div>
              )}

              <Button 
                onClick={handleWhatsAppClick}
                className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-500 hover:to-green-600 text-white text-lg font-bold py-6 shadow-lg shadow-green-900/30 border border-green-500/30"
              >
                <span className="flex items-center gap-2">
                    <img src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg" className="w-6 h-6" alt="wp"/>
                    {price ? 'HEMEN REZERVASYON YAP' : 'FİYAT AL & KİRALA'}
                </span>
              </Button>

            </CardContent>
          </Card>
        </div>

        {/* Text Content */}
        <div className="space-y-6 text-white animate-accordion-down order-2 lg:order-1 text-center lg:text-left">
          <div className="inline-block px-4 py-1 border border-yellow-500/50 rounded-full bg-yellow-500/10 backdrop-blur-md mb-2">
            <span className="text-yellow-400 text-xs font-bold tracking-widest uppercase">Premium VIP Hizmet</span>
          </div>
          <h1 className="text-4xl md:text-7xl font-extrabold tracking-tight leading-tight drop-shadow-2xl">
            Antalya'nın <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-200 via-yellow-500 to-yellow-200 animate-pulse">
              VIP Ayrıcalığını
            </span> <br />
            Keşfedin
          </h1>
          <p className="text-lg text-gray-300 max-w-lg mx-auto lg:mx-0 drop-shadow-md">
            Havalimanı ve otel transferlerinizde konfor, güven ve lüksü bir arada sunuyoruz. 
            Son model Mercedes VIP araçlarımızla 7/24 hizmetinizdeyiz.
          </p>
          <div className="flex flex-wrap gap-4 pt-4 justify-center lg:justify-start">
             <div className="flex items-center gap-2 bg-white/5 backdrop-blur-md px-4 py-3 rounded-xl border border-white/10 hover:border-yellow-500/50 transition-colors shadow-lg">
                <Check className="text-yellow-500 h-5 w-5" />
                <span className="text-sm font-medium">Havalimanı Karşılama</span>
             </div>
             <div className="flex items-center gap-2 bg-white/5 backdrop-blur-md px-4 py-3 rounded-xl border border-white/10 hover:border-yellow-500/50 transition-colors shadow-lg">
                <Check className="text-yellow-500 h-5 w-5" />
                <span className="text-sm font-medium">Ücretsiz İçecek</span>
             </div>
             <div className="flex items-center gap-2 bg-white/5 backdrop-blur-md px-4 py-3 rounded-xl border border-white/10 hover:border-yellow-500/50 transition-colors shadow-lg">
                <Check className="text-yellow-500 h-5 w-5" />
                <span className="text-sm font-medium">Özel Şoför</span>
             </div>
          </div>
        </div>

      </div>
      
      {/* Sliding Image Gallery */}
      <ImageMarquee />
    </div>
  );
};

export default Hero;
